import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import './GroupsManagement.css';
//Components
import Button from '../../../components/Button/Button';
import Table from '../../../components/Table/Table';
import Filters from '../../../components/Filters/Filters';
import Badge from '../../../components/Badge/Badge';
// Icons
import editIcon from '../../../assets/editIcon.png';
import editInactive from '../../../assets/tokens/editInactive.png';
import eyeOn from '../../../assets/tokens/eyeOn.png';
import toggleActive from '../../../assets/toggleActive.png';
import toggleInactive from '../../../assets/toggleInactive.png';
import trashActive from '../../../assets/tokens/trashActive.png';
import trashInactive from '../../../assets/tokens/trashInactive.png';
// Utils
import { modulePermissions } from '../../../utils/modulePermissions';
import { formatDate } from '../../../utils/utils';
// services
import { groupService } from '../../../services/Group';
//hanlde
import HandleResponse from '../../../utils/HandleResponse';
import HandleAlert from '../../../utils/HandleAlert';
import Tooltip from '../../../components/Tooltip/Tooltip';

const GroupsManagement = () => {
  const navigate = useNavigate();
  const { searchTerm } = useSelector((state) => state.search);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [groupsData, setGroupsData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [columnsShare, setColumnsShare] = useState(0);
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    fetchOrganizations();
    fetchColumns();
    fetchGroupsData(filters, 1);
  }, [searchTerm]);

  const fetchOrganizations = async () => {
    try {
      const response = await groupService.getOrganizations();
      if(response.length > 0) {
        const data = response.map(org => ({
          value: org.id,
          label: org.name
        }));
        setOrganizations(data);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchColumns = () => {
    let count = 1;
    const columnsData = [
      { header: 'Group name', field: 'name' },
      { header: 'Group owner', field: 'owner' },
      { header: 'Number of members', field: 'members' },
      { header: 'Type of organization', field: 'organization_type' },
      { header: 'Creation date', field: 'creationDate' },
      { 
        header: 'Status', 
        render: (row) => (
          <Badge status={row.status} />
        )
      }
    ];

    if(modulePermissions.hasGroupsPermission('edit')) {
      columnsData.push({
        header: '',
        render: (row) => (
          <Tooltip text="Edit group">
            <button className="action-btn" onClick={() => handleEdit(row)}>
              <img src={row.status === 'deleted' ? editInactive : editIcon} alt="Edit" />
            </button>
          </Tooltip>
        )
      });
      count++;
    }

    columnsData.push({
      header: '',
      render: (row) => (
        <Tooltip text="View details of this group">
          <button className="action-btn" onClick={() => handleView(row)}>
            <img src={eyeOn} alt="View" />
          </button>
        </Tooltip>
      )
    });

    if(modulePermissions.hasGroupsPermission('activate_deactivate')) {
      columnsData.push({
        header: '',
        render: (row) => (
          <Tooltip text="Activate or Deactivate this group">
            <button className="action-btn" onClick={() => handleToggle(row)}>
              <img src={row.status === 'active' ? toggleActive : toggleInactive} alt="Toggle" />
            </button>
          </Tooltip>
        )
      });
      count++;
    }

    if(modulePermissions.hasGroupsPermission('delete')) {
      columnsData.push({
        header: '',
        render: (row) => (
          <Tooltip text="Delete this group">
            <button className="action-btn" onClick={() => handleDelete(row)}>
              <img src={row.status === 'active' ? trashActive : trashInactive} alt="Delete" />
            </button>
          </Tooltip>
        )
      });
      count++;
    }

    setColumns(columnsData);
    setColumnsShare(count);
  };

  const fetchGroupsData = async (filterValues = {}, page = 1) => {
    try {
      const status = filterValues.status ? filterValues.status.toLowerCase() : null;
      const type_organization = filterValues.type ? filterValues.type.value : null;
      const search = searchTerm ? searchTerm : null;
      let start_date = filterValues.creationDate ? filterValues.creationDate.start_date : null;
      let end_date = filterValues.creationDate ? filterValues.creationDate.end_date : null;
      const min_members = filterValues.members ? filterValues.members : null;
      if(start_date) start_date = start_date.split('T')[0];
      if(end_date) end_date = end_date.split('T')[0];
      const params = {
        page: page,
        items_per_page: 10,
        order: 'asc',
        ...(status && { status }),
        ...(type_organization && { type_organization }),
        ...(search && { search }),
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(min_members && { min_members })
      };
      const response = await groupService.groupsList(params);
      if(response && response.total > 0) {
        setTotalItems(response.data.total);
        const groups = response.data.map(group => ({
          ...group,
          id: group.id,
          name: group.name,
          members: group.members_count,
          creationDate: formatDate(group.created_at),
          /*owner: group.participant.name,
          creationDate: formatDate(group.group.created_at),
          status: group.group.status*/
        }));
        setGroupsData(groups);
        setTotalItems(response.total);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleAddGroup = () => {
    navigate('/groups/new-group', {
      state: { 
        group: null,
        action: 'add' 
      }
    });
  };

  const handleEdit = (row) => {
    if(row.status === 'deleted') {
      HandleResponse.showErrorModal({
        title: { text: 'Edit', highlight: 'group' },
        message: 'The group is already deleted.',
        confirmText: 'Understood',
        onConfirm: () => {
          HandleResponse.hideModal();
        }
      });
      return;
    };
    const edtiData = {
      id: row.id
    };
    navigate(`/groups/edit-group/${row.id}`, {
      state: { 
        edtiData: edtiData,
        action: 'edit' 
      }
    });
  };

  const handleView = (row) => {
    navigate(`/groups/group-details/${row.id}`, {
      state: { group: row }
    });
  };

  const handleToggle = (row) => {
    const title = row.status === 'active' ? 'Deactivate' : 'Activate';
    if(row.status === 'deleted') {
      HandleResponse.showErrorModal({
        title: { text: title, highlight: 'group' },
        message: 'The group is already deleted.',
        confirmText: 'Understood',
        onConfirm: () => {
          HandleResponse.hideModal();
        }
      });
      return;
    };
    HandleAlert.showConfirmationAlert({
      title: { text: title, highlight: 'group' },
      message: 'Do you want to continue with the action?',
      onConfirm: () => {
        HandleAlert.hideAlert();
        handleToggleStatus(row);
      }
    });
  };

  const handleDelete = (row) => {
    if(row.status === 'deleted') {
      HandleResponse.showErrorModal({
        title: { text: 'Delete', highlight: 'group' },
        message: 'The group is already deleted.',
        confirmText: 'Understood',
        onConfirm: () => {
          HandleResponse.hideModal();
        }
      });
      return;
    };
    HandleAlert.showConfirmationAlert({
      title: { text: 'Delete', highlight: 'group' },
      message: 'Do you want to continue with the action?',
      onConfirm: () => {
        HandleAlert.hideAlert();
        handleDeleteStatus(row);
      }
    });
  };

  const handleDownload = () => {
    try {
      const status = filters.status ? filters.status.toLowerCase() : null;
      const type_organization = filters.type ? filters.type.value : null;
      const search = searchTerm ? searchTerm : null;
      let start_date = filters.creationDate ? filters.creationDate.start_date : null;
      let end_date = filters.creationDate ? filters.creationDate.end_date : null;
      const min_members = filters.members ? filters.members : null;
      if(start_date) start_date = start_date.split('T')[0];
      if(end_date) end_date = end_date.split('T')[0];
      const params = {
        page: 1,
        items_per_page: 1000,
        order: 'asc',
        download: 'excel',
        ...(status && { status }),
        ...(type_organization && { type_organization }),
        ...(search && { search }),
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(min_members && { min_members })
      };
      groupService.downloadGroups(params);
    } catch (error) {
      console.error('Error downloading groups:', error);
    }
  };

  const handleToggleStatus = async (row) => {
    try {
      await groupService.changeStatusGroup(row.id, row.status === 'active' ? false : true);
      const title = row.status === 'active' ? 'Deactivate' : 'Activate';
      HandleResponse.showSuccessModal({
        title: { text: title, highlight: 'group' },
        message: 'The group has been successfully ' + title + '.',
        confirmText: 'Accept',
        onConfirm: () => {
          fetchGroupsData(filters, currentPage);
          HandleResponse.hideModal();
        }
      });
    } catch (error) {
      HandleResponse.showErrorModal({
        title: { text: 'Toggle', highlight: 'group' },
        message: 'An error has occurred while toggling the group.',
        confirmText: 'Understood',
        onConfirm: () => {
          HandleResponse.hideModal();
        }
      });
    }
  };

  const handleDeleteStatus = async (row) => {
    try {
      if(row.status === 'inactive') {
        HandleResponse.showErrorModal({
          title: { text: 'Delete', highlight: 'group' },
          message: 'The group is already inactive.',
          confirmText: 'Understood',
          onConfirm: () => {
            HandleResponse.hideModal();
          }
        });
        return;
      };
      const response = await groupService.deleteGroup(row.id);
      HandleResponse.showSuccessModal({
        title: { text: 'Delete', highlight: 'group' },
        message: 'The group has been successfully deleted.',
        confirmText: 'Accept',
        onConfirm: () => {
          fetchGroupsData(filters, currentPage);
          HandleResponse.hideModal();
        }
      });
    } catch (error) {
      HandleResponse.showErrorModal({
        title: { text: 'Delete', highlight: 'group' },
        message: 'An error has occurred while deleting the group.',
        confirmText: 'Understood',
        onConfirm: () => {
          HandleResponse.hideModal();
        }
      });
    }
  };

  return (
    <div className="groups-management-page">
      <div className="groups-management-container">
        <div className="actions-bar">
          <Filters
            filters={[
              {
                id: 'status',
                label: 'Status',
                type: 'checkbox',
                options: ['Active', 'Pending', 'Inactive']
              },
              {
                id: 'creationDate',
                label: 'Creation date',
                type: 'dateRange',
                placeholder: 'dd/mm/yyyy - dd/mm/yyyy'
              },
              {
                id: 'type',
                label: 'Type of organization',
                type: 'dropdown',
                options: organizations
              },
              {
                id: 'members',
                label: 'Number of members',
                type: 'number',
                placeholder: 'Enter number of members'
              }
            ]}
            onApplyFilters={(selectedFilters) => {
              setFilters(selectedFilters);
              // Here you would call your API with the selected filters
              fetchGroupsData(selectedFilters, currentPage);
            }}
          />
          <div className="action-buttons">
            {modulePermissions.hasGroupsPermission('create') && (
              <Button
                children="Add group"
                onClick={handleAddGroup}
                variant="primary"
                borderRadius={10}
              />
            )}
            {modulePermissions.hasGroupsPermission('download') && (
              <Button
                children="Download"
                onClick={handleDownload}
                variant="warning"
                borderRadius={10}
              />
            )}
          </div>
        </div>

        <div className="table-container">
          <Table
            columns={columns}
            data={groupsData}
            currentPage={currentPage}
            totalItems={totalItems}
            columnsShare={columnsShare}
            onPageChange={(page) => {
              setCurrentPage(page);
              fetchGroupsData(filters, page);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default GroupsManagement;